import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Snackbar,
  Alert,
  Tooltip,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsActive as NotificationsActiveIcon,
  ErrorOutline as ErrorIcon,
  PriorityHigh as PriorityHighIcon,
  Info as InfoIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { formatDateTimeBR } from '../../utils/dateUtils';

interface NotificationItem {
  id: string;
  type: 'INTERNAL_ERROR' | 'PRIORITY_URGENT' | 'SYSTEM';
  title: string;
  message: string;
  link?: string | null;
  referenceId?: string | null;
  isRead: boolean;
  createdAt: string;
  readAt?: string | null;
}

const POLL_INTERVAL_MS = 30000; // 30 segundos

const getIconForType = (type: NotificationItem['type']) => {
  switch (type) {
    case 'INTERNAL_ERROR':
      return <ErrorIcon fontSize="small" sx={{ color: '#dc2626' }} />;
    case 'PRIORITY_URGENT':
      return <PriorityHighIcon fontSize="small" sx={{ color: '#ea580c' }} />;
    default:
      return <InfoIcon fontSize="small" sx={{ color: '#2563eb' }} />;
  }
};

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const lastSeenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const open = Boolean(anchorEl);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await apiService.getUnreadNotificationsCount();
      setUnreadCount(count);
    } catch {
      // silencioso
    }
  }, []);

  const fetchAndDetectNew = useCallback(async () => {
    try {
      const list: NotificationItem[] = await apiService.getNotifications(20, false);

      // Detectar novas (apenas após inicialização)
      if (initializedRef.current) {
        const newOnes = list.filter(
          n => !lastSeenIdsRef.current.has(n.id) && !n.isRead,
        );
        if (newOnes.length > 0) {
          const first = newOnes[0];
          setToast({ open: true, message: `${first.title}: ${first.message}` });
        }
      }
      lastSeenIdsRef.current = new Set(list.map(n => n.id));
      initializedRef.current = true;

      const unread = list.filter(n => !n.isRead).length;
      setUnreadCount(prev => (prev !== unread ? unread : prev));
    } catch {
      // silencioso
    }
  }, []);

  // Polling
  useEffect(() => {
    fetchAndDetectNew();
    const interval = setInterval(fetchAndDetectNew, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchAndDetectNew]);

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    try {
      const list = await apiService.getNotifications(20, false);
      setNotifications(list);
      lastSeenIdsRef.current = new Set(list.map((n: NotificationItem) => n.id));
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickNotification = async (n: NotificationItem) => {
    try {
      if (!n.isRead) {
        await apiService.markNotificationAsRead(n.id);
        setNotifications(prev => prev.map(x => (x.id === n.id ? { ...x, isRead: true } : x)));
        setUnreadCount(c => Math.max(0, c - 1));
      }
    } catch {
      // ignora erro de marcar como lida
    }
    handleClose();
    if (n.link) {
      navigate(n.link);
    } else {
      navigate('/notifications');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // ignora
    }
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/notifications');
  };

  return (
    <>
      <Tooltip title="Notificações">
        <IconButton
          onClick={handleOpen}
          sx={{
            ml: 1,
            '&:hover': {
              backgroundColor: 'rgba(212, 175, 55, 0.15)',
            },
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
            overlap="circular"
          >
            {unreadCount > 0 ? (
              <NotificationsActiveIcon sx={{ color: '#D4AF37' }} />
            ) : (
              <NotificationsIcon sx={{ color: 'text.secondary' }} />
            )}
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: { width: 380, maxWidth: '95vw', maxHeight: 520 },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Notificações
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title="Marcar todas como lidas">
              <Button
                size="small"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllAsRead}
                sx={{ textTransform: 'none' }}
              >
                Marcar todas
              </Button>
            </Tooltip>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <NotificationsIcon sx={{ fontSize: 40, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2">Nenhuma notificação</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 380, overflow: 'auto' }}>
            {notifications.map((n, idx) => (
              <React.Fragment key={n.id}>
                <ListItemButton
                  onClick={() => handleClickNotification(n)}
                  sx={{
                    backgroundColor: n.isRead ? 'transparent' : 'rgba(212, 175, 55, 0.08)',
                    alignItems: 'flex-start',
                    py: 1.25,
                  }}
                >
                  <Box sx={{ mr: 1.5, mt: 0.5 }}>{getIconForType(n.type)}</Box>
                  <ListItemText
                    primary={
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: n.isRead ? 500 : 700, lineHeight: 1.3 }}
                        >
                          {n.title}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" component="div" sx={{ color: 'text.primary', mt: 0.25 }}>
                          {n.message}
                        </Typography>
                        <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.25 }}>
                          {formatDateTimeBR(n.createdAt)}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
                {idx < notifications.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
          <Button size="small" onClick={handleViewAll} sx={{ textTransform: 'none' }}>
            Ver todas
          </Button>
        </Box>
      </Popover>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          icon={<NotificationsActiveIcon />}
          onClose={() => setToast({ open: false, message: '' })}
          variant="filled"
          sx={{ minWidth: 300 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationBell;
